(() => {
  'use strict';

  const productCache = new Map();
  const money = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP'
  });

  const normalise = (value) => String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');

  const productPathFromLink = (link) => {
    try {
      const url = new URL(link.href, window.location.origin);
      return url.pathname.includes('/products/')
        ? url.pathname.replace(/\/$/, '')
        : null;
    } catch (error) {
      return null;
    }
  };

  const loadProduct = (link) => {
    const path = productPathFromLink(link);
    if (!path) return Promise.resolve(null);

    if (!productCache.has(path)) {
      productCache.set(path, fetch(`${path}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      })
        .then((response) => response.ok ? response.json() : null)
        .catch(() => null));
    }

    return productCache.get(path);
  };

  const variantImageSource = (variant) => {
    const featuredImage = variant && variant.featured_image;
    if (typeof featuredImage === 'string') return featuredImage;
    if (featuredImage && featuredImage.src) return featuredImage.src;
    if (variant && variant.featured_media && variant.featured_media.preview_image) {
      return variant.featured_media.preview_image.src || null;
    }
    return null;
  };

  const updateImage = (container, product, variant) => {
    const image = container.querySelector('img');
    const source = variantImageSource(variant);
    if (!image || !source) return;

    const url=new URL(source.startsWith('//') ? 'https:'+source : source,location.origin);
    const predictive=Boolean(container.closest('.bw-predictive'));
    const widths=predictive?[90,135,180]:[400,600,900];
    image.srcset=widths.map(width=>{const sized=new URL(url);sized.searchParams.set('width',String(width));return sized.href+' '+width+'w';}).join(', ');
    image.sizes=predictive?'72px':'(min-width:1100px) 25vw, (min-width:650px) 33vw, 50vw';
    url.searchParams.set('width',String(widths[widths.length-1]));image.src=url.href;

    const optionName = variant.public_title || variant.title;
    if (product && product.title && optionName && optionName !== 'Default Title') {
      image.alt = `${product.title} – ${optionName}`;
    }
  };

  const updateLink = (link, variant) => {
    try {
      const url = new URL(link.href, window.location.origin);
      url.searchParams.set('variant', String(variant.id));
      link.href = url.toString();
    } catch (error) {
      // Leave the original link intact if it cannot be parsed.
    }
  };

  const updateMainSearchCard = (card, product, variant) => {
    updateLink(card, variant);
    updateImage(card, product, variant);

    const sku = card.querySelector('.bw-product-sku');
    const skuValue = sku && sku.querySelector('span');
    if (sku && skuValue && variant.sku) {
      sku.hidden = false;
      skuValue.textContent = variant.sku;
    }

    const price = card.querySelector('.bw-product__price');
    if (price && Number.isFinite(Number(variant.price))) {
      price.textContent = money.format(Number(variant.price) / 100);
    }
  };

  const updatePredictiveCard = (link, product, variant) => {
    updateLink(link, variant);
    updateImage(link, product, variant);

    const sku = link.querySelector('.bw-predictive__skus');
    if (sku && variant.sku) {
      sku.textContent = `SKU: ${variant.sku}`;
    }

    const price = link.querySelector('.bw-predictive__price');
    if (price && Number.isFinite(Number(variant.price))) {
      price.textContent = money.format(Number(variant.price) / 100);
      if (variant.available === false) {
        const soldOut = document.createElement('small');
        soldOut.textContent = 'Sold out';
        price.appendChild(document.createTextNode(' '));
        price.appendChild(soldOut);
      }
    }
  };

  const currentSearchQuery = (element) => {
    const formInput = element.closest('form')?.querySelector('input[name="q"]');
    if (formInput && formInput.value.trim()) return formInput.value.trim();

    const active = document.activeElement;
    if (active && active.matches && active.matches('input[name="q"]') && active.value.trim()) {
      return active.value.trim();
    }

    const populated = Array.from(document.querySelectorAll('input[name="q"]'))
      .find((input) => input.value.trim());
    if (populated) return populated.value.trim();

    return new URLSearchParams(window.location.search).get('q') || '';
  };

  const processMainSearchCards = () => {
    document.querySelectorAll('.bw-products--search .bw-product[href*="/products/"]')
      .forEach((card) => {
        let variantId = null;
        try {
          variantId = new URL(card.href, window.location.origin).searchParams.get('variant');
        } catch (error) {
          return;
        }

        if (!variantId || card.dataset.bwVariantCard === variantId) return;
        card.dataset.bwVariantCard = variantId;

        loadProduct(card).then((product) => {
          if (!product || !Array.isArray(product.variants)) return;
          const variant = product.variants.find((item) => String(item.id) === String(variantId));
          if (variant) updateMainSearchCard(card, product, variant);
        });
      });
  };

  const processPredictiveCards = () => {
    document.querySelectorAll('.bw-predictive__link[href*="/products/"]')
      .forEach((link) => {
        const query = normalise(currentSearchQuery(link));
        if (query.length < 3 || link.dataset.bwVariantQuery === query) return;
        link.dataset.bwVariantQuery = query;

        loadProduct(link).then((product) => {
          if (!product || !Array.isArray(product.variants)) return;

          const exact = product.variants.find((variant) => normalise(variant.sku) === query);
          const partial = product.variants.filter((variant) => {
            const sku = normalise(variant.sku);
            return sku && (sku.includes(query) || query.includes(sku));
          });
          const variant = exact || (partial.length === 1 ? partial[0] : null);
          if (variant) updatePredictiveCard(link, product, variant);
        });
      });
  };

  let scheduled = false;
  const processCards = () => {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(() => {
      scheduled = false;
      processMainSearchCards();
      processPredictiveCards();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', processCards, { once: true });
  } else {
    processCards();
  }

  document.addEventListener('input', (event) => {
    if (event.target && event.target.matches('input[name="q"]')) processCards();
  });

  const observeSearch=()=>{document.querySelectorAll('.bw-global-search__results,[data-bw-predictive-results],.bw-products--search').forEach(root=>new MutationObserver(processCards).observe(root,{childList:true,subtree:true}));};
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',observeSearch,{once:true});else observeSearch();
})();
